import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import axios from 'axios';
import { styled } from '@this/constants/themes';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import { StylesProvider, ThemeProvider } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Container from '@material-ui/core/Container';
import Snackbar from '@material-ui/core/Snackbar';
import type { AlertProps } from '@material-ui/lab/Alert';
import MuiAlert from '@material-ui/lab/Alert';
import Drawer from '@material-ui/core/Drawer';
import { setupMixpanel } from '@this/lib/mixpanel_utils';
import { setupBugsnag } from '@this/lib/bugsnag';
import type UserJson from '@this/domain/user/user_json';
import User from '@this/domain/user/user';
import AppointContext from '@this/components/appoint/appoint_context';
import type Calendar from '@this/domain/appoint/calendar';
import Utils from '@this/domain/appoint/utils';
import Header from './header';
import DrawerMenu from './drawer_menu';
import Dashboard from './dashboard/dashboard';
import UsersSignUp from './users/sign_up';
import UsersSignIn from './users/sign_in';
// import UsersEdit from './users/edit';
import UsersInvitationAccept from './users/invitation_accept';
// './adjustments/new_with_three_steps/new'に変更することでステップ有りのフォームに切り替えられる
import AdjustmentsNew from './adjustments/new/new';
import AdjustmentsShow from './adjustments/show/show';
import OpenMeetingsNew from './open_meetings/new/new';
import OpenMeetingsShow from './open_meetings/show/show';
import OpenMeetingsEdit from './open_meetings/edit/edit';
import BookingsShow from './open_meetings/bookings/show/show';
import InquiriesNew from './inquiries/new';
import Organization from './organization/organization';
import Calendars from './calendars/calendars';
import ExternalConnections from './external_connections/external_connections';
import type { SnackbarContent } from './common';
import muiTheme from './mui_theme';
// import ResetPassword from './users/reset_password';

interface UserResponse {
  user?: UserJson;
  google_oauth_path?: string;
  outlook_oauth_path?: string;
}

let basename = '';
if (location.pathname.indexOf('/aicalendar') !== -1) {
  basename = '/aicalendar';
}

const Appoint = () => {
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarContent, setSnackbarContent] = useState<SnackbarContent>({
    message: '',
    severity: 'success'
  });
  const [user, setUser] = useState<User | null>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [calendars, setCalendars] = useState<Calendar[]>([]);

  const [googleOauthPath, setGoogleOauthPath] = useState<string>('');
  const [outlookOauthPath, setOutlookOauthPath] = useState<string>('');

  const [fixWithCalendar, setFixWithCalendar] = useState<boolean | null>(null);

  const menuButtonRef = useRef(null);

  const fetchUser = async () => {
    try {
      const res = await axios.get<UserResponse>('/aicalendar/user.json');
      if (res.data.user) {
        const user = new User(res.data.user);
        setUser(user);
        setupMixpanel(user);
        setupBugsnag(user);
      } else {
        setGoogleOauthPath(res.data.google_oauth_path || '');
        setOutlookOauthPath(res.data.outlook_oauth_path || '');
      }
    } catch (e) {
      setUser(null);
      setupMixpanel(null);
    }
  };

  // マウント時に1回発動
  useEffect(() => {
    const distinctId = Utils.getParam('distinct_id');
    if (!_.isEmpty(distinctId)) {
      Utils.mpIdentify(distinctId);
      const newUrl = location.href.replace(/\?distinct_id=.*/, '');
      window.history.pushState(null, '', newUrl);
    }
    fetchUser();
  }, []);

  const contextValue = {
    user,
    calendars,
    setCalendars,
    setOpenSnackbar: (open: boolean) => {
      setOpenSnackbar(open);
    },
    setSnackbarContent: (content: SnackbarContent) => {
      setSnackbarContent(content);
    },
    onUserSignedIn: () => {
      fetchUser();
    },
    onUserSignedOut: () => {
      setUser(null);
      setOpenDrawer(false);
    },
    googleOauthPath,
    outlookOauthPath,
    env:
      location.host.includes('localhost') || location.host.includes('ngrok.io')
        ? 'development'
        : location.host.includes('stg')
        ? 'staging'
        : 'production',
    isMobile: useMediaQuery('(max-width:767px)'),
    fixWithCalendar
  };

  const handleClose = () => {
    setOpenSnackbar(false);
  };

  try {
    return (
      <ThemeProvider theme={muiTheme}>
        <StylesProvider injectFirst>
          <AppointContext.Provider value={contextValue}>
            <Router basename={basename}>
              <Wrapper isMobile={contextValue.isMobile}>
                <Header
                  onDrawerOpen={() => setOpenDrawer(true)}
                  menuButtonRef={menuButtonRef}
                  setFixWithCalendar={setFixWithCalendar}
                />
                <Main>
                  <Switch>
                    <Route
                      exact
                      path="/"
                      render={() => (
                        <Dashboard
                          menuButtonRef={menuButtonRef}
                          openHeadDrawer={openDrawer}
                          setOpenHeadDrawer={setOpenDrawer}
                        />
                      )}
                    />
                    <Route exact path="/users/sign_up" component={UsersSignUp} />
                    <Route exact path="/users/sign_in" component={UsersSignIn} />
                    {/* <Route exact path="/users/edit" component={UsersEdit} /> */}
                    {/* <Route exact path="/users/password/edit" component={ResetPassword} /> */}
                    <Route exact path="/users/invitation/accept" component={UsersInvitationAccept} />
                    <Route exact path="/adjustments/new" component={AdjustmentsNew} />
                    <Route exact path="/inquiries/new" component={InquiriesNew} />
                    <Route
                      path="/adjustments/:key"
                      render={props => <AdjustmentsShow {...props} setFixWithCalendar={setFixWithCalendar} />}
                    />
                    <Route exact path="/open_meetings/new" component={OpenMeetingsNew} />
                    <Route path="/open_meetings/:openMeetingKey/bookings/:key" component={BookingsShow} />
                    <Route
                      exact
                      path="/open_meetings/:key/edit"
                      render={props => <OpenMeetingsEdit {...props} />}
                    />
                    <Route
                      path="/open_meetings/:key"
                      render={props => <OpenMeetingsShow {...props} setFixWithCalendar={setFixWithCalendar} />}
                    />
                    <Route path="/organization" component={Organization} />
                    <Route path="/calendars" component={Calendars} />
                    <Route path="/external_connections" component={ExternalConnections} />
                  </Switch>
                </Main>
              </Wrapper>
              {snackbarContent.message && (
                <Snackbar
                  open={openSnackbar}
                  autoHideDuration={6000}
                  onClose={handleClose}
                  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                  style={{ marginTop: '64px' }}
                >
                  <Alert onClose={handleClose} severity={snackbarContent.severity} style={{ width: '600px' }}>
                    {snackbarContent.message.split('\n').map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </Alert>
                </Snackbar>
              )}
              {/* ダッシュボードは特殊なDrawer */}
              {(!contextValue.isMobile || ['/aicalendar', '/aicalendar/'].indexOf(location.pathname) === -1) && (
                <Drawer
                  open={openDrawer}
                  anchor={contextValue.isMobile ? 'left' : 'right'}
                  onClose={() => setOpenDrawer(false)}
                >
                  <DrawerMenu onNavigate={() => setOpenDrawer(false)} setOpen={setOpenDrawer} />
                </Drawer>
              )}
            </Router>
          </AppointContext.Provider>
        </StylesProvider>
      </ThemeProvider>
    );
  } catch (e) {
    if (typeof bugsnagClient !== 'undefined') {
      bugsnagClient.notify(e);
    }
    throw e;
  }
};

const Wrapper = styled.div<{ isMobile: boolean }>`
  flex-grow: 9999;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  @import url(//fonts.googleapis.com/earlyaccess/notosansjapanese.css);
  font-family: 'Roboto', 'Noto Sans Japanese', 'Helvetica', 'Arial', sans-serif;
  letter-spacing: 0.4px;
  -webkit-font-smoothing: antialiased;

  ${props =>
    props.isMobile &&
    `
    padding-top: 54px;
    box-sizing: border-box;
  `}
`;

const Main = styled.main`
  flex-grow: 9999;
  display: flex;
  flex-direction: column;
`;

const Alert = (props: AlertProps) => {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
};

export const SubPageContainer = styled(Container)`
  padding: 40px 24px 64px;
  background-color: #dae3e9;
  flex-grow: 9999;
`;

export default Appoint;
